import { FilterMatchMode } from "primereact/api"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { DataTable } from "primereact/datatable"
import { InputText } from "primereact/inputtext"
import React, { useEffect, useState } from "react"
import AddDialogBoxWithDataTable from "../addDialogBoxWithDataTable"
import { useParams } from "react-router-dom"
import {
	assignUserToGroupService,
	unassignUserFromGroupService,
} from "services/groupService"
import { ToastContent, toast } from "react-toastify"
import { AxiosError } from "axios"
import DynamicModal from "components/DynamicModal"
import { IconField } from "primereact/iconfield"
import { InputIcon } from "primereact/inputicon"

export type APIAssignedUserResponse = {
	first_name: string
	last_name: string
}[]

type IGroupUserListProps = {
	assigned_users: APIAssignedUserResponse
	unassigned_users: APIAssignedUserResponse
	fetchGroupData: (id: string | undefined) => Promise<void>
}

const GroupUserList = ({
	assigned_users,
	unassigned_users,
	fetchGroupData,
}: IGroupUserListProps) => {
	const [filters, setFilters] = useState<{ [s: string]: any }>()
	const [globalFilterValue, setGlobalFilterValue] = useState("")
	const [addModalVisibility, setAddModalVisibility] = useState(false)
	const [first, setFirst] = useState(0); // State to control the first page
	const [sortField, setSortField] = useState(""); // State to control the first page
	const [sortOrder, setSortOrder] = useState<any>(""); // State to control the first page
	const [rowsPerPage, setRowsPerPage] = useState(5); // State to control the rows per page
	const params = useParams()

	const [selectedUsersToUnassign, setSelectedUsersToUnassign] =
		useState<Record<string, any>>()

	const initFilters = () => {
		setFilters({
			global: { value: null, matchMode: FilterMatchMode.CONTAINS },
		})
		setGlobalFilterValue("")
	}

	const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFirst(0);
		const value = e?.target?.value
		let _filters = { ...filters }

		_filters["global"]!.value = value

		setFilters(_filters)
		setGlobalFilterValue(value)
	}
	const handleSort = (event: any) => {
		setSortField(event.sortField)
		setSortOrder(event.sortOrder)
		setFirst(0);  //Move to page 1
		// dispatch(setFirstVoice(0)); // Move to page 1
	};
	useEffect(() => {
		fetchGroupData(params.id)
		initFilters()
	}, [fetchGroupData, params.id])

	const unassignUserHandler = async (id: string) => {
		try {
			const { response, error } = await unassignUserFromGroupService({
				group_id: Number(params.id),
				user_id: Number(id),
			})

			if (error) {
				throw error
			}
			if (response?.error) {
				throw response?.error
			}
			toast.success(response?.message, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		} catch (error) {
			if (error instanceof AxiosError || error instanceof Error) {
				toast.error(error.message)
			}
			return toast.error(error as ToastContent<unknown>, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		}

		fetchGroupData(params.id)
	}

	const unassignMultipleUserHandler = async (
		users: Record<string, any> | undefined
	) => {
		try {
			const { response, error } = await unassignUserFromGroupService({
				group_id: Number(params.id),
				user_id: users?.map((body: { id: any }) => body.id),
			})

			if (error) {
				throw error
			}
			if (response?.error) {
				throw response?.error
			}
			toast.success(response?.message, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		} catch (error) {
			if (error instanceof AxiosError || error instanceof Error) {
				toast.error(error.message)
			}
			return toast.error(error as ToastContent<unknown>, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		}

		fetchGroupData(params.id)
	}

	const assignUserToGroup = async (users: Record<string, any>) => {
		try {
			const { response, error } = await assignUserToGroupService({
				group_id: Number(params.id),
				user_id: users.map(({ id }: { id: string; name: string }) => {
					return Number(id)
				}),
			})

			if (error) {
				throw error
			}
			if (response?.error) {
				throw response?.error
			}
			toast.success(response?.message, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
			setAddModalVisibility(false)
		} catch (error) {
			if (error instanceof AxiosError || error instanceof Error) {
				toast.error(error.message)
			}
			return toast.error(error as ToastContent<unknown>, {
				style: {
					backgroundColor: '#F1F1F1', // Grey background
				}
			})
		}

		fetchGroupData(params.id)
	}

	const handleCloseUserModal = () => {
		setSelectedUsersToUnassign([])
	}

	if (!params.id) return <div></div>
	return (
		<div className="user-management-section">
			<div className='d-flex align-items-center flex-wrap justify-content-end mb-3'>
				<div className="d-flex justify-content-between align-items-center">
					<div className="d-flex align-items-center dialog-filter">
						<div className="d-flex align-items-center mr-3">
							<IconField iconPosition="right" className='user-input'>
								<InputIcon className="pi pi-search"></InputIcon>
								<InputText
									value={globalFilterValue}
									onChange={onGlobalFilterChange}
									placeholder="Search"
								/>
							</IconField>
						</div>
						<Button
							className="outline-icon-btn mr-3"
							type="button"
							onClick={() => setAddModalVisibility(true)}
						>
							<i className="fa-solid fa-user-plus mr-2"></i> Assign User
						</Button>
						<AddDialogBoxWithDataTable
							name="Users"
							header={"Add users to the group"}
							onSave={(selectedUsersToAdd) => {
								assignUserToGroup(selectedUsersToAdd)
								fetchGroupData(params.id)
								setAddModalVisibility(false)
							}}
							onCancel={() => setAddModalVisibility(false)}
							addModalVisibility={addModalVisibility}
							data={unassigned_users}
						/>

						<Button
							className="outline-icon-btn"
							type="button"
							disabled={!selectedUsersToUnassign?.length}
							data-bs-toggle="modal" data-bs-target="#unAssigUsersModal"
						>
							<i className="fa-solid fa-user-minus mr-2"></i> Unassign User
						</Button>
						<DynamicModal text={"Are you sure you want to unassign users ?"} modalKey={"unAssigUsersModal"} functionToCall={unassignMultipleUserHandler} funcParams={selectedUsersToUnassign} setSelectedRolesToUnassign={setSelectedUsersToUnassign} handleCloseModal={handleCloseUserModal} />
					</div>
				</div>
			</div>
			<div className="datatable-wrapper">
				<DataTable
					selection={selectedUsersToUnassign}
					onSelectionChange={(e) => setSelectedUsersToUnassign(e.value)}
					selectionPageOnly
					paginator
					filters={filters}
					globalFilterFields={["first_name", "last_name"]}
					first={first}
					onPage={(e) => { setFirst(e.first); setRowsPerPage(e.rows) }}
					rows={rowsPerPage}
					sortField={sortField}
					sortOrder={sortOrder}
					sortMode="single"
					onSort={handleSort}
					rowsPerPageOptions={[5, 10, 25]}
					stripedRows
					value={assigned_users}
					emptyMessage="No user records found"
				>
					<Column
						selectionMode="multiple"
						headerStyle={{ width: "3rem" }}
					></Column>
					<Column field="first_name" header="First Name" sortable></Column>
					<Column field="last_name" header="Last Name" sortable></Column>
					<Column
						header="Action"
						body={ActionButton(unassignUserHandler, handleCloseUserModal)}
					></Column>
				</DataTable>
			</div>
		</div>
	)
}

export default GroupUserList

function ActionButton(unassignUserHandler: (id: string) => {}, handleCloseUserModal: any) {
	return (rowData: { [s: string]: string }) => {
		return (
			<>
				<span
					className="status-tag delete"
					data-bs-toggle="modal" data-bs-target={`#unAssigUserModal-${rowData.id}`}
					onClick={(e) => e.stopPropagation()}
				>
					Unassign
				</span>
				<DynamicModal text={"Are you sure you want to unassign user ?"} modalKey={`unAssigUserModal-${rowData.id}`} functionToCall={unassignUserHandler} funcParams={rowData.id} setSelectedRolesToUnassign={"single"} handleCloseModal={handleCloseUserModal} />
			</>
		)
	}
}
